import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, ChevronRight, Pencil, Play, Trash2, Users2, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { Input } from "@/components/ui/field";
import { StatusBadge } from "@/components/status-badge";
import {
    useCancelSchedule,
    useDeleteCampaign,
    useRunCampaign,
    useScheduleCampaign,
    type Campaign,
} from "@/lib/campaigns";

function formatDateTime(value: string | null): string {
    return value ? new Date(value).toLocaleString("vi-VN", { dateStyle: "short", timeStyle: "short" }) : "";
}

/** Giá trị cho <input type="datetime-local"> theo GIỜ ĐỊA PHƯƠNG (không phải UTC). */
function toLocalInputValue(date: Date): string {
    const pad = (value: number) => String(value).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

const DEFAULT_LEAD_MINUTES = 5;

function defaultScheduleValue(): string {
    return toLocalInputValue(new Date(Date.now() + DEFAULT_LEAD_MINUTES * 60_000));
}

export function CampaignCard({ campaign, onEdit }: { campaign: Campaign; onEdit: () => void }) {
    const navigate = useNavigate();
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduleAt, setScheduleAt] = useState(defaultScheduleValue);
    const [scheduleError, setScheduleError] = useState<string | null>(null);

    function openSchedule() {
        // Mở ra là có sẵn mốc +5 phút, khỏi phải gõ từ đầu
        setScheduleAt(defaultScheduleValue());
        setScheduleError(null);
        setScheduleOpen(true);
    }

    function submitSchedule() {
        const when = new Date(scheduleAt);

        if (Number.isNaN(when.getTime())) {
            setScheduleError("Chưa chọn thời điểm gửi");
            return;
        }
        if (when.getTime() <= Date.now()) {
            setScheduleError("Thời điểm gửi phải ở tương lai");
            return;
        }

        setScheduleError(null);
        schedule.mutate(
            { id: campaign.id, scheduleAt: when.toISOString() },
            {
                onSuccess: () => setScheduleOpen(false),
                onError: (error) =>
                    setScheduleError(error instanceof Error ? error.message : "Đặt lịch thất bại"),
            },
        );
    }

    const run = useRunCampaign();
    const schedule = useScheduleCampaign();
    const cancelSchedule = useCancelSchedule();
    const remove = useDeleteCampaign();

    return (
        <Card>
            <CardBody className="space-y-3">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2">
                            <h3 className="truncate font-medium text-ink">{campaign.name}</h3>
                            <StatusBadge status={campaign.status} />
                        </div>
                        <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{campaign.messageText}</p>

                        {campaign.media.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {campaign.media.map((item) => (
                                    <div
                                        key={item.id}
                                        className="relative size-14 overflow-hidden rounded-lg border border-line bg-surface-muted"
                                        title={item.filename}
                                    >
                                        <img
                                            src={item.thumbnailUrl ?? item.url}
                                            alt={item.filename}
                                            loading="lazy"
                                            className="size-full object-cover"
                                        />
                                        {item.kind === "VIDEO" && (
                                            <span className="absolute inset-0 flex items-center justify-center bg-ink/35">
                                                <Video className="size-4 text-white" aria-hidden />
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        <p className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-ink-muted">
                            <span className="flex items-center gap-1">
                                <Users2 className="size-3.5" />
                                {campaign.targetCount} nhóm
                            </span>
                            {campaign.scheduleAt && (
                                <span className="flex items-center gap-1 text-brand">
                                    <CalendarClock className="size-3.5" />
                                    {formatDateTime(campaign.scheduleAt)}
                                </span>
                            )}
                        </p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                        <Button
                            size="sm"
                            loading={run.isPending}
                            disabled={campaign.status === "RUNNING" || campaign.targetCount === 0}
                            onClick={() =>
                                run.mutate(campaign.id, { onSuccess: (result) => navigate(`/runs/${result.runId}`) })
                            }
                        >
                            <Play className="size-4" />
                            Gửi ngay
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => (scheduleOpen ? setScheduleOpen(false) : openSchedule())} title="Hẹn lịch">
                            <CalendarClock className="size-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={onEdit} title="Sửa">
                            <Pencil className="size-4" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            title="Xoá"
                            onClick={() => confirm(`Xoá chiến dịch "${campaign.name}"?`) && remove.mutate(campaign.id)}
                        >
                            <Trash2 className="size-4 text-danger" />
                        </Button>
                    </div>
                </div>

                {run.error && <p className="text-sm text-danger">{run.error.message}</p>}

                {scheduleOpen && (
                    <div className="space-y-2 rounded-lg bg-surface-muted p-3">
                        <div className="flex flex-wrap items-end gap-2">
                        <Input
                            className="flex-1"
                            type="datetime-local"
                            value={scheduleAt}
                            min={toLocalInputValue(new Date())}
                            onChange={(event) => {
                                setScheduleAt(event.target.value);
                                setScheduleError(null);
                            }}
                        />
                        <Button size="sm" loading={schedule.isPending} onClick={submitSchedule}>
                            Đặt lịch
                        </Button>
                        {campaign.scheduleAt && (
                            <Button
                                size="sm"
                                variant="secondary"
                                loading={cancelSchedule.isPending}
                                onClick={() => cancelSchedule.mutate(campaign.id, { onSuccess: () => setScheduleOpen(false) })}
                            >
                                Huỷ lịch
                            </Button>
                        )}
                        </div>

                        {scheduleError && <p className="text-sm text-danger">{scheduleError}</p>}
                    </div>
                )}

                {campaign.lastRun && (
                    <button
                        type="button"
                        onClick={() => navigate(`/runs/${campaign.lastRun!.id}`)}
                        className="flex w-full items-center gap-3 rounded-lg border border-line px-3 py-2 text-left text-xs hover:bg-surface-muted"
                    >
                        <StatusBadge status={campaign.lastRun.status} />
                        <span className="text-ink-muted">
                            {campaign.lastRun.sentCount}/{campaign.lastRun.totalCount} đã gửi
                            {campaign.lastRun.failedCount > 0 && ` · ${campaign.lastRun.failedCount} lỗi`}
                            {campaign.lastRun.uncertainCount > 0 && ` · ${campaign.lastRun.uncertainCount} chưa rõ`}
                        </span>
                        <span className="ml-auto flex items-center gap-0.5 text-brand">
                            Xem chi tiết
                            <ChevronRight className="size-3.5" aria-hidden />
                        </span>
                    </button>
                )}
            </CardBody>
        </Card>
    );
}
